/**
 * Create or edit a background (name, picture, active flag, assigned stores).
 * The picture is uploaded (or removed) after the record is saved, then the app
 * config is refreshed.
 */
import {
  useState,
  type ChangeEvent,
  type SubmitEvent,
  type MouseEvent,
} from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Store } from '@/shared/config/app-config';
import { appConfigQueryKey, fetchAppConfig } from '@/shared/config/app-config';
import { Dialog } from '@/shared/ui/Dialog';
import closeIcon from '@/assets/images/close.svg';
import uploadIcon from '@/assets/images/upload.svg';
import { AssignedStoresField } from './AssignedStoresField';
import { backgroundPictureUrl, orderStoreIds } from './logic';
import {
  createBackground,
  deleteBackgroundPicture,
  updateBackground,
  uploadBackgroundPicture,
  type Asset,
} from './api';
import { readAsDataUrl } from '@/shared/lib/read-file';

export interface BackgroundDialogProps {
  item: Asset | null;
  stores: readonly Store[];
  onClose: (result: 'Confirm' | 'Cancel') => void;
}

const NAME_MAX = 100;
const isValidName = (value: string) =>
  value.length >= 1 && value.length <= NAME_MAX;

export function BackgroundDialog({
  item,
  stores,
  onClose,
}: Readonly<BackgroundDialogProps>) {
  const creating = !item;
  const queryClient = useQueryClient();
  const [name, setName] = useState(item?.name ?? '');
  const [active, setActive] = useState(item?.active ?? true);
  const [assignedStores, setAssignedStores] = useState<string[]>(
    (item?.assignedStores ?? []).filter(
      (id): id is string => typeof id === 'string',
    ),
  );
  // Picture state: `touched` so that a removal is sent to the API too.
  const [picture, setPicture] = useState<File | null>(null);
  const [pictureBase64, setPictureBase64] = useState<string | null>(null);
  const [pictureTouched, setPictureTouched] = useState(false);
  const [serverPictureHidden, setServerPictureHidden] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Red border once the field has been edited and is invalid.
  const [nameDirty, setNameDirty] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const model = {
        name,
        active,
        assignedStores: orderStoreIds(assignedStores, stores),
      };
      let id: string;
      try {
        const saved = item
          ? await updateBackground(item.id, model)
          : await createBackground(model);
        id = item?.id ?? saved.id;
      } catch (error) {
        throw new Error(
          creating
            ? 'Failed to create background'
            : 'Failed to update background',
          { cause: error },
        );
      }
      try {
        if (pictureTouched && id) {
          if (picture) await uploadBackgroundPicture(id, picture, 'background');
          else await deleteBackgroundPicture(id, 'background');
        }
      } catch (error) {
        throw new Error('Failed to upload/remove background pictures', {
          cause: error,
        });
      }
      await fetchAppConfig();
      await queryClient.invalidateQueries({ queryKey: appConfigQueryKey });
    },
    onSuccess: () => onClose('Confirm'),
    onError: (error: Error) => setErrorMessage(error.message),
  });

  const onSubmit = (event: SubmitEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setErrorMessage(null);
    if (!isValidName(name)) {
      setErrorMessage('Please fix the errors');
      return;
    }
    save.mutate();
  };

  const onPictureChosen = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setPicture(file);
    setPictureTouched(true);
    setServerPictureHidden(true);
    setPictureBase64(await readAsDataUrl(file));
  };

  const onPictureRemoved = (event: MouseEvent) => {
    event.preventDefault();
    if (picture === null && pictureBase64 === null && serverPictureHidden)
      return;
    setPicture(null);
    setPictureBase64(null);
    setPictureTouched(true);
    setServerPictureHidden(true);
  };

  const serverPicture = backgroundPictureUrl(item);
  const showServerPicture = !!item && !serverPictureHidden && !!serverPicture;

  return (
    <Dialog
      title={creating ? 'background creation' : 'background edit'}
      onClose={() => onClose('Cancel')}
      testId="settings-background-dialog"
    >
      <form className="form popin-form" onSubmit={onSubmit} noValidate>
        <div className="bs-row">
          <div className="col-span-5">
            <div className="mb-2">
              <label className="field-label" htmlFor="background-name">
                Background Name
              </label>
              <input
                id="background-name"
                data-testid="settings-background-form-name"
                className={`field ${nameDirty && !isValidName(name) ? 'invalid' : ''}`}
                name="name"
                placeholder="Name"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  setNameDirty(true);
                }}
                required
              />
              <small
                hidden={!submitted || isValidName(name)}
                data-testid="settings-background-form-name-error"
              >
                Wrong name
              </small>
            </div>
            <div className="mb-2">
              <span className="field-label" id="background-picture-label">
                Background image
              </span>
              <input
                className="field field-file-input"
                name="backgroundPicture"
                id="background-picture"
                data-testid="settings-background-form-picture"
                type="file"
                accept=".png, .jpg, .jpeg, .gif"
                aria-labelledby="background-picture-label"
                onChange={(event) => void onPictureChosen(event)}
              />
              <label
                htmlFor="background-picture"
                className="field field-file-label"
              >
                {showServerPicture && (
                  <img
                    className="icon"
                    src={serverPicture}
                    alt=""
                    data-testid="settings-background-form-picture-preview"
                    onError={() => setServerPictureHidden(true)}
                  />
                )}
                {picture && pictureBase64 && (
                  <img
                    className="icon"
                    src={pictureBase64}
                    alt=""
                    data-testid="settings-background-form-picture-preview"
                  />
                )}
                {!picture && !showServerPicture && (
                  <img className="upload-icon" src={uploadIcon} alt="" />
                )}
                {(picture || showServerPicture) && (
                  <button
                    type="button"
                    className="remove-icon"
                    onClick={onPictureRemoved}
                    aria-label="Remove picture"
                    data-testid="settings-background-form-picture-remove-button"
                  >
                    <img src={closeIcon} alt="" />
                  </button>
                )}
              </label>
            </div>
            <div className="mb-2 mt-4">
              <div className="styled-input-square styled-input-single">
                <input
                  type="checkbox"
                  name="active"
                  id="background-active"
                  data-testid="settings-background-form-active"
                  checked={active}
                  onChange={(event) => setActive(event.target.checked)}
                />
                <label htmlFor="background-active">Activate Background</label>
              </div>
            </div>
            {errorMessage && (
              <div
                className="alert mb-0"
                role="alert"
                data-testid="settings-background-form-error"
              >
                <span className="error-msg">{errorMessage}</span>
              </div>
            )}
            <div className="mt-6 flex justify-center">
              <button
                className="button button-form"
                type="submit"
                disabled={save.isPending}
                data-testid="settings-background-form-save-button"
              >
                Save
              </button>
            </div>
          </div>
          <div className="col-span-5">
            <AssignedStoresField
              stores={stores}
              selectedIds={assignedStores}
              onChange={setAssignedStores}
              frameless
              testId="settings-background-form-assigned-stores"
            />
          </div>
        </div>
      </form>
    </Dialog>
  );
}
